/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React, {FC, Suspense, lazy} from 'react'
import {Redirect, Switch, Route} from 'react-router-dom'
import {shallowEqual, useSelector} from 'react-redux'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {PrivateRoutes} from './PrivateRoutes'
import {Logout, AuthPage} from '../modules/auth'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {RootState} from '../../setup'
import {MasterInit} from '../../_metronic/layout/MasterInit'
import ApplicationLandingPage from '../modules/application/component/landing/ApplicationLandingPage'
import WizardStartPage from '../modules/application/component/wizard/WizardStartPage'
import CoApplicantInviteVerifyPage from '../modules/application/component/wizard/CoApplicantInviteVerifyPage'
import SecureSubmissionViewPage from '../modules/application/component/submission/SecureSubmissionViewPage'
import RenterHomePage from '../modules/application/component/renter-home/RenterHomePage'
import RenterProfileEditPage from '../modules/application/component/renter-home/RenterProfileEditPage'
import SubmitToPropertyPage from '../modules/application/component/renter-home/SubmitToPropertyPage'

const PmReviewPage = lazy(() => import('../modules/application/component/pm-review/PmReviewPage'))
const PmEnterEmailPage = lazy(
  () => import('../modules/application/component/pm-review/PmEnterEmailPage')
)
const UnsubscribePage = lazy(
  () => import('../modules/application/component/wizard/UnsubscribePage')
)

const Routes: FC = () => {
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as any
  const isAdmin = user?.userRoles?.includes('Administrator') === true

  const renderAdminEnterEmailPage = (location: any) => {
    if (!user) {
      return (
        <Redirect
          to={{
            pathname: '/auth/login',
            state: {from: location},
          }}
        />
      )
    }

    if (!isAdmin) {
      return <Redirect to='/error/404' />
    }

    return (
      <Suspense fallback={<div className='container py-4'>Loading...</div>}>
        <PmEnterEmailPage mode='admin' />
      </Suspense>
    )
  }

  const renderAuthRedirect = (location: any) => {
    const from = location?.state?.from
    if (from?.pathname) {
      return <Redirect to={`${from.pathname}${from.search || ''}${from.hash || ''}`} />
    }

    return <Redirect to='/' />
  }

  return (
    <>
      <Switch>
        <Route path='/application-landing' render={() => <ApplicationLandingPage />} />

        <Route exact path='/application-submission/:token' component={SecureSubmissionViewPage} />
        <Route exact path='/my-doora-card' render={() => <RenterHomePage />} />
        <Route exact path='/my-doora-card/submit' component={SubmitToPropertyPage} />
        <Route exact path='/my-doora-card/edit/:stepParam' component={RenterProfileEditPage} />
        <Route
          exact
          path='/renter-home'
          render={({location}) => <Redirect to={`/my-doora-card${location.search}`} />}
        />
        <Route
          exact
          path='/pm/submission/:token/enter-email'
          render={() => (
            <Suspense fallback={<div className='container py-4'>Loading...</div>}>
              <PmEnterEmailPage />
            </Suspense>
          )}
        />
        <Route
          exact
          path='/pm/submission/:token'
          render={() => (
            <Suspense fallback={<div className='container py-4'>Loading PM review...</div>}>
              <PmReviewPage />
            </Suspense>
          )}
        />

        <Route exact path='/application-wizard'>
          <Redirect to='/application-wizard/create-account' />
        </Route>
        <Route exact path='/application-wizard/start'>
          <Redirect to='/application-wizard/create-account' />
        </Route>

        {/* Legacy wizard step aliases */}
        <Route exact path='/application-wizard/verifymobile'>
          <Redirect to='/application-wizard/verify-mobile' />
        </Route>
        <Route exact path='/application-wizard/account'>
          <Redirect to='/application-wizard/application-type' />
        </Route>
        <Route exact path='/application-wizard/noaccount'>
          <Redirect to='/application-wizard/application-type' />
        </Route>
        <Route exact path='/application-wizard/confirm'>
          <Redirect to='/application-wizard/review' />
        </Route>
        <Route exact path='/application-wizard/last'>
          <Redirect to='/application-wizard/review' />
        </Route>

        {/* Wizard route with step param */}
        <Route
          exact
          path='/application-wizard/coapp-invite/:inviteId'
          component={CoApplicantInviteVerifyPage}
        />
        <Route
          exact
          path='/application-wizard/unsubscribe/:token'
          render={() => (
            <Suspense fallback={<div className='container py-4'>Loading...</div>}>
              <UnsubscribePage />
            </Suspense>
          )}
        />
        <Route
          exact
          path='/application-wizard/household-warning'
          render={({location}) => (
            <Redirect to={{pathname: '/application-wizard/profile', search: location.search}} />
          )}
        />
        <Route exact path='/application-wizard/:stepParam' component={WizardStartPage} />

        <Route
          exact
          path='/admin/submission/:token/enter-email'
          render={({location}) => renderAdminEnterEmailPage(location)}
        />

        <Route path='/error' component={ErrorsPage} />
        <Route path='/logout' component={Logout} />

        {!user ? (
          <Route path='/auth' component={AuthPage} />
        ) : (
          <Route path='/auth' render={({location}) => renderAuthRedirect(location)} />
        )}

        {!user ? (
          /*Redirect to `/auth` when user is not authorized*/
          <Route
            render={({location}) => (
              <Redirect
                to={{
                  pathname: '/auth/login',
                  state: {from: location},
                }}
              />
            )}
          />
        ) : (
          <>
            <MasterLayout>
              <PrivateRoutes activePages={user.activePages} />
            </MasterLayout>
          </>
        )}
      </Switch>
      <MasterInit />
    </>
  )
}

export {Routes}
